import React, { useState, useEffect } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Calendar, Clock, User, MapPin, RefreshCw, Search, ChevronLeft, ChevronRight
} from 'lucide-react';

const statusColors = {
  SCHEDULED: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  CONFIRMED: 'bg-green-500/10 text-green-600 border-green-500/20',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  OPEN: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

function ShiftCard({ shift }) {
  const shiftDate = new Date(shift.date);
  const isToday = shiftDate.toDateString() === new Date().toDateString();
  const isTomorrow = shiftDate.toDateString() === new Date(Date.now() + 86400000).toDateString();

  return (
    <Card className="hover:border-primary/30 transition-colors" data-testid={`shift-card-${shift.date}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Date */}
          <div className="flex items-center gap-3 sm:w-40 flex-shrink-0">
            <div className={`h-12 w-12 rounded-lg flex flex-col items-center justify-center ${
              isToday ? 'bg-primary text-primary-foreground' : 
              isTomorrow ? 'bg-amber-500 text-white' : 'bg-muted'
            }`}>
              <span className="text-xs font-medium uppercase">
                {shiftDate.toLocaleDateString('en-AU', { weekday: 'short' })}
              </span>
              <span className="text-lg font-bold leading-none">
                {shiftDate.getDate()}
              </span>
            </div>
            <div>
              <p className="font-medium text-sm">
                {isToday ? 'Today' : isTomorrow ? 'Tomorrow' : 
                  shiftDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}
              </p>
              <p className="text-xs text-muted-foreground">
                {shiftDate.toLocaleDateString('en-AU', { year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold">{shift.serviceType || 'Support Session'}</p>
              <Badge variant="outline" className={statusColors[shift.status] || ''}>
                {shift.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {shift.startTime} - {shift.endTime}
              </div>
              {shift.staffName && (
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {shift.staffName}
                </div>
              )}
            </div>
            {shift.notes && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{shift.notes}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ShiftSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PortalShiftsPage() {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [daysAhead, setDaysAhead] = useState(14);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get(`/portal/participant/shifts?days_ahead=${daysAhead}`);
      setShifts(res.data.data || []);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [daysAhead]);

  const filteredShifts = shifts.filter(shift => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      shift.serviceType?.toLowerCase().includes(query) ||
      shift.staffName?.toLowerCase().includes(query) ||
      shift.notes?.toLowerCase().includes(query)
    );
  });

  // Group shifts by date
  const groupedShifts = filteredShifts.reduce((groups, shift) => {
    const date = shift.date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(shift);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedShifts).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Upcoming Supports</h1>
          <p className="text-muted-foreground">Your scheduled support sessions</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchShifts} disabled={loading} data-testid="refresh-shifts">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search supports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-shifts"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Show next:</span>
              <div className="flex gap-1">
                {[7, 14, 30].map((days) => (
                  <Button
                    key={days}
                    variant={daysAhead === days ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDaysAhead(days)}
                    data-testid={`days-${days}`}
                  >
                    {days}d
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shifts List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <ShiftSkeleton key={i} />)}
        </div>
      ) : filteredShifts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">No upcoming supports</p>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search' : `No supports scheduled in the next ${daysAhead} days`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 sticky top-0 bg-background py-1">
                {new Date(date).toLocaleDateString('en-AU', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long',
                  year: 'numeric'
                })}
              </h3>
              <div className="space-y-3">
                {groupedShifts[date].map((shift, idx) => (
                  <ShiftCard key={idx} shift={shift} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {!loading && filteredShifts.length > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Showing {filteredShifts.length} support{filteredShifts.length !== 1 ? 's' : ''} in the next {daysAhead} days
              </span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  <span className="text-muted-foreground">Scheduled</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="text-muted-foreground">Confirmed</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
